import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, ChevronDown, Truck, Clock, Package, Zap } from 'lucide-react';

const routes = [
  { from: "Mumbai", to: "Delhi", distance: "1,400 km", time: "24-36 hrs", type: "Express Corridor", tag: "Most Popular", services: ["Full Load", "Part Load", "Express"], stops: ["Surat", "Vadodara", "Ahmedabad", "Jaipur"] },
  { from: "Bangalore", to: "Chennai", distance: "350 km", time: "6-10 hrs", type: "Tech Corridor", tag: "Fastest", services: ["Full Load", "Part Load"], stops: ["Hosur", "Krishnagiri", "Vellore"] },
  { from: "Delhi", to: "Kolkata", distance: "1,500 km", time: "36-48 hrs", type: "Eastern Gateway", tag: "Heavy Load", services: ["Full Load", "Express"], stops: ["Agra", "Kanpur", "Varanasi", "Patna"] },
  { from: "Hyderabad", to: "Pune", distance: "560 km", time: "10-14 hrs", type: "Industrial Route", tag: "Daily Runs", services: ["Full Load", "Part Load"], stops: ["Gulbarga", "Solapur"] },
  { from: "Ahmedabad", to: "Jaipur", distance: "650 km", time: "12-18 hrs", type: "Trade Corridor", tag: "Reliable", services: ["Full Load", "Express"], stops: ["Palanpur", "Abu Road", "Ajmer"] },
  { from: "Chennai", to: "Kochi", distance: "700 km", time: "14-20 hrs", type: "Coastal Route", tag: "Scenic", services: ["Part Load", "Full Load"], stops: ["Vellore", "Salem", "Coimbatore", "Palakkad"] },
];

const tagColors = {
  "Most Popular": "bg-brand-orange text-white",
  "Fastest": "bg-green-100 text-green-700 border border-green-300",
  "Heavy Load": "bg-blue-100 text-blue-700 border border-blue-300",
  "Daily Runs": "bg-purple-100 text-purple-700 border border-purple-300",
  "Reliable": "bg-yellow-100 text-yellow-700 border border-yellow-300",
  "Scenic": "bg-teal-100 text-teal-700 border border-teal-300",
};

const RouteCoverage = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section id="routes" className="section-padding bg-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-brand-orange/6 blur-[120px] pointer-events-none" />

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="mb-16">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="text-brand-orange font-bold tracking-widest uppercase mb-3 text-sm">
            Pan-India Network
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black uppercase leading-tight text-brand-black">
            EXPLORE OUR <span className="text-brand-orange text-glow">ROUTES.</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="text-brand-muted mt-4 text-lg max-w-xl">
            Premium logistics corridors across India — click any route to see details.
          </motion.p>
        </div>

        <div className="space-y-3">
          {routes.map((route, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07, type: 'spring', stiffness: 80 }}
              className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                openIndex === i
                  ? 'border-brand-orange/40 bg-orange-50'
                  : 'border-black/8 bg-white hover:border-black/20'
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center gap-2 sm:gap-4 px-4 sm:px-6 py-4 sm:py-5 text-left"
              >
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                    <MapPin size={13} className="text-brand-orange shrink-0" />
                    <span className="font-black text-brand-black text-sm sm:text-base md:text-lg truncate max-w-[70px] sm:max-w-none">{route.from}</span>
                  </div>
                  <div className="flex-1 flex items-center gap-1 mx-1 sm:mx-2 min-w-0">
                    <div className="h-[1px] flex-1 bg-brand-orange/30" />
                    <Truck size={13} className="text-brand-orange shrink-0" />
                    <div className="h-[1px] flex-1 bg-brand-orange/30" />
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                    <MapPin size={13} className="text-brand-muted shrink-0" />
                    <span className="font-black text-brand-black text-sm sm:text-base md:text-lg truncate max-w-[70px] sm:max-w-none">{route.to}</span>
                  </div>
                </div>

                <div className="hidden sm:flex items-center gap-4 sm:gap-6 shrink-0">
                  <div className="text-right">
                    <p className="text-xs text-brand-muted uppercase tracking-widest">Distance</p>
                    <p className="font-bold text-brand-black text-sm">{route.distance}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-brand-muted uppercase tracking-widest">Transit</p>
                    <p className="font-bold text-brand-orange text-sm">{route.time}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${tagColors[route.tag]}`}>
                    {route.tag}
                  </span>
                </div>

                <ChevronDown size={18} className={`text-brand-orange shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 border-t border-black/5 pt-4 sm:pt-6">
                      <div>
                        <p className="text-xs text-brand-muted uppercase tracking-widest mb-3 flex items-center gap-2">
                          <MapPin size={12} /> Key Stops
                        </p>
                        <div className="flex flex-col gap-2">
                          {route.stops.map((stop, j) => (
                            <div key={j} className="flex items-center gap-2 text-sm text-brand-black">
                              <div className="w-1.5 h-1.5 rounded-full bg-brand-orange/60" />
                              {stop}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-xs text-brand-muted uppercase tracking-widest mb-3 flex items-center gap-2">
                          <Package size={12} /> Services
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {route.services.map((svc, j) => (
                            <span key={j} className="px-3 py-1.5 bg-brand-orange/10 border border-brand-orange/20 rounded-lg text-xs font-bold text-brand-orange">
                              {svc}
                            </span>
                          ))}
                        </div>
                        <div className="mt-4 flex items-center gap-2 text-sm text-brand-muted">
                          <Clock size={14} className="text-brand-orange" />
                          <span>{route.time} transit</span>
                        </div>
                      </div>

                      <div className="flex flex-col justify-between gap-4">
                        <div>
                          <p className="text-xs text-brand-muted uppercase tracking-widest mb-1">Route Type</p>
                          <p className="font-bold text-brand-black">{route.type}</p>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => document.getElementById('book')?.scrollIntoView({ behavior: 'smooth' })}
                          className="w-full py-3 bg-brand-orange rounded-xl font-black tracking-widest uppercase text-sm text-white orange-glow flex items-center justify-center gap-2"
                        >
                          <Zap size={16} /> Book This Route
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RouteCoverage;
